import { Box, Button, Checkbox, Chip, FormControlLabel, Stack, Typography } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import SortingHandler from 'src/common/tableRelated/SortingHandler'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import MentorPastEventsListing from 'src/mentor/components/MentorEventsPage/components/bookingListing/MentorPastEventsListing'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getUserRoles } from 'src/mentor/layout/utils'
import SupportBookedEventRescheduleDrawer from 'src/supportPortal/components/bookings/SupportBookedEventRescheduleDrawer'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import {
  canViewSupportBookings,
  canViewSupportVcAuditBookings,
  getAllowedSupportBookedEventTypeOptions,
  SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS,
} from 'src/supportPortal/access/accessMatrix'

const SORT_OPTIONS = [
  { label: 'Start Time', value: 'startTime' },
  { label: 'Booking Time', value: 'createdTime' },
]

function mergeEventTypeOptions(baseOptions = [], extraOptions = []) {
  const merged = []
  ;[...baseOptions, ...extraOptions].forEach((option) => {
    if (!option?.id || merged.some((existing) => existing.id === option.id)) {
      return
    }
    merged.push(option)
  })
  return merged
}

function getRecordingPolicySourceLabel(source) {
  switch (source) {
    case 'event':
      return 'Event override'
    case 'patient':
      return 'Patient default'
    case 'default-off':
      return 'Default off'
    default:
      return null
  }
}

function RecordingPreferenceInlineActions({ bookedEvent, onSuccess }) {
  const [isUpdating, setIsUpdating] = useState(false)

  if (!bookedEvent?.canManageRecordingPreference) {
    return null
  }

  const isRecordingEnabled = bookedEvent?.resolvedRecordingEnabled === true
  const sourceLabel = getRecordingPolicySourceLabel(bookedEvent?.resolvedRecordingPolicySource)

  const handleToggle = async () => {
    if (!bookedEvent?.id || isUpdating) {
      return
    }

    setIsUpdating(true)
    const loadingToast = toast.loading('Updating recording preference...')
    const apiUrl = isRecordingEnabled
      ? '/api/secure/cc/support/events/mark-event-as-not-recordable'
      : '/api/secure/cc/support/events/mark-event-as-recordable'

    try {
      const response = await putSPDataWithParams(apiUrl, null, {
        'booked-event-id': bookedEvent.id,
      })

      if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
        toast.update(loadingToast, {
          render: isRecordingEnabled
            ? 'Recording disabled for this event'
            : 'Recording enabled for this event',
          type: 'success',
          isLoading: false,
          autoClose: 2500,
        })
        onSuccess?.()
        return
      }

      toast.update(loadingToast, {
        render: response?.data?.message || 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } catch (error) {
      toast.update(loadingToast, {
        render: 'Could not update recording preference',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.25,
        backgroundColor: 'rgba(16, 185, 129, 0.04)',
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Recording
          </Typography>
          <Chip
            size="small"
            color={isRecordingEnabled ? 'success' : 'default'}
            variant={isRecordingEnabled ? 'filled' : 'outlined'}
            label={isRecordingEnabled ? 'Enabled' : 'Disabled'}
          />
          {sourceLabel ? (
            <Chip
              size="small"
              variant="outlined"
              label={sourceLabel}
            />
          ) : null}
        </Stack>

        <Button
          size="small"
          variant="outlined"
          onClick={handleToggle}
          disabled={isUpdating}
        >
          {isUpdating
            ? 'Updating...'
            : isRecordingEnabled
              ? 'Turn Off For This Event'
              : 'Turn On For This Event'}
        </Button>
      </Stack>
    </Box>
  )
}

function VcAuditInlineActions({ bookedEvent, hasVcAuditAccess }) {
  const navigate = useNavigate()

  if (!hasVcAuditAccess || !bookedEvent?.vcAuditVisible) {
    return null
  }

  const canPlayLive = bookedEvent?.vcAuditCanViewLive === true
  const recordingCount = Number(bookedEvent?.vcAuditRecordingCount || 0)
  const canPlayRecording = bookedEvent?.vcAuditCanViewRecordings === true && recordingCount > 0
  const latestRecordingId = bookedEvent?.vcAuditLatestRecordingId

  if (!canPlayLive && !canPlayRecording && !bookedEvent?.vcAuditIsLive && recordingCount <= 0) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.25,
        backgroundColor: 'rgba(99, 102, 241, 0.03)',
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            VC Audit
          </Typography>
          {bookedEvent?.vcAuditIsLive ? (
            <Chip
              size="small"
              color="error"
              icon={<FiberManualRecordIcon />}
              label={bookedEvent?.vcAuditLiveBadgeLabel || 'Live'}
            />
          ) : null}
          {recordingCount > 0 ? (
            <Chip
              size="small"
              color="secondary"
              variant="outlined"
              label={`${recordingCount} recording${recordingCount > 1 ? 's' : ''}`}
            />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canPlayLive ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayCircleOutlineIcon />}
              onClick={() =>
                navigate(`/support-home/booked-events/${bookedEvent?.id}/live`, {
                  state: { from: '/support-home/booked-events' },
                })
              }
            >
              Play Live
            </Button>
          ) : null}
          {canPlayRecording ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VideoLibraryOutlinedIcon />}
              onClick={() => {
                const path = latestRecordingId
                  ? `/support-home/booked-events/${bookedEvent?.id}/recordings/${latestRecordingId}`
                  : `/support-home/booked-events/${bookedEvent?.id}/recordings`
                navigate(path, {
                  state: { from: '/support-home/booked-events' },
                })
              }}
            >
              Play Recording
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  )
}

function LegacyBookedEventsView({ showHeader = true, eventTypeOptions = [], hasVcAuditAccess = false }) {
  const [selectedEventType, setSelectedEventType] = useState(
    eventTypeOptions[0]?.id || null,
  )
  const [sortingHandler, setSortingHandler] = useState(null)
  const [showOnlyPaidEvents, setShowOnlyPaidEvents] = useState(true)
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [selectedEventForEngagement, setSelectedEventForEngagement] = useState(null)
  const [rescheduleDrawerBooking, setRescheduleDrawerBooking] = useState(null)
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])
  const listingRef = useRef(null)

  useEffect(() => {
    if (
      eventTypeOptions.length > 0 &&
      !eventTypeOptions.some((option) => option.id === selectedEventType)
    ) {
      setSelectedEventType(eventTypeOptions[0].id)
    } else if (eventTypeOptions.length === 0 && selectedEventType !== null) {
      setSelectedEventType(null)
    }
  }, [eventTypeOptions, selectedEventType])

  const handleEngagementClick = (bookedEvent) => {
    setSelectedEventForEngagement(bookedEvent)
    setEngagementDialogOpen(true)
  }

  return (
    <>
      {showHeader ? <SupportHeader heading={'Bookings & Appointments'} /> : null}
      <div
        style={{
          background: '#35304a',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '10px',
          gap: '12px',
        }}
      >
        <div>
          {eventTypeOptions.length > 0 ? (
            <BaseUITabsComponent
              value={selectedEventType}
              onChange={setSelectedEventType}
              tabs={eventTypeOptions}
              getTabLabel={(tab) => tab?.title}
              getTabId={(tab) => tab?.id}
            />
          ) : null}
        </div>
        <div
          className="d-flex align-items-center justify-content-end gap-2"
          style={{ color: 'white !important' }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyPaidEvents}
                onChange={() => setShowOnlyPaidEvents(!showOnlyPaidEvents)}
                sx={{
                  color: 'white !important',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
              />
            }
            label="Hide payment pending events"
            sx={{
              '& .MuiFormControlLabel-label': {
                color: 'white !important',
              },
            }}
          />
          <SortingHandler
            sortValue={sortingHandler}
            setSortValue={setSortingHandler}
            getOptionLabel={(option) => option?.label}
            getOptionValue={(option) => option?.value}
            sortOptions={SORT_OPTIONS}
            outerDivStyle={{
              marginLeft: '10px',
              color: 'white',
              fontSize: '14px',
              alignItems: 'center',
            }}
            buttonColor="primary"
            buttonVariant="contained"
          />
        </div>
      </div>
      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        <MentorPastEventsListing
          ref={listingRef}
          key={`${selectedEventType || 'all'}-${showOnlyPaidEvents}`}
          listingApiSignature="/api/secure/cc/support/events/all/paginated-unified"
          showMentorName
          hidePortalVoiceCallAction
          cancelAppointmentApiSignature="/api/secure/cc/support/events/actions/cancel"
          skipCancelCheckInGuard
          listOfPaymentLinksApiSignature="/api/secure/cc/support/events/user-payment-link/get"
          eventType={selectedEventType || undefined}
          sortingHandler={sortingHandler}
          showOnlyPaidEvents={showOnlyPaidEvents}
          logsApiEndpoint="/api/secure/cc/support/events/logs/get"
          filterByBookedStatus={null}
          eventTypeOptions={eventTypeOptions}
          onRescheduleBooking={(bookedEvent) => setRescheduleDrawerBooking(bookedEvent)}
          onEngagementClick={hasEngagementPermission ? handleEngagementClick : undefined}
          rightSideCustomComponent={({ event, defaultActions }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <RecordingPreferenceInlineActions
                bookedEvent={event}
                onSuccess={() => listingRef.current?.resetData?.()}
              />
              <VcAuditInlineActions bookedEvent={event} hasVcAuditAccess={hasVcAuditAccess} />
              {defaultActions}
              <SupportContactActions
                patientId={event?.patientId}
                practitionerId={event?.mentorId}
                bookedEventId={event?.id}
                clinicBrandId={event?.clinicBrandId}
                clinicId={event?.clinicId}
                targetRole="patient"
                eventType={event?.eventType || selectedEventType}
                title=""
                compact
                uiSurface="support-booked-events-strip"
                sx={{ minWidth: 240 }}
              />
            </Box>
          )}
        />
      </div>

      {hasEngagementPermission && (
        <EngagementSettingsDialog
          open={engagementDialogOpen}
          onClose={() => {
            setEngagementDialogOpen(false)
            setSelectedEventForEngagement(null)
          }}
          targetType="booked-event"
          target={selectedEventForEngagement}
          onSuccess={() => {}}
        />
      )}

      <SupportBookedEventRescheduleDrawer
        open={Boolean(rescheduleDrawerBooking)}
        bookedEvent={rescheduleDrawerBooking}
        onClose={() => setRescheduleDrawerBooking(null)}
        onSuccess={() => {
          setRescheduleDrawerBooking(null)
          listingRef.current?.resetData?.()
        }}
      />
    </>
  )
}

export default function BookedEvents() {
  const userRoles = getUserRoles()
  const hasVcAuditAccess = canViewSupportVcAuditBookings(userRoles)
  const hasSupportBookingsAccess = canViewSupportBookings(userRoles)
  const allowedSupportEventTypes = useMemo(() => {
    const baseOptions = getAllowedSupportBookedEventTypeOptions(userRoles)
    if (!hasVcAuditAccess) {
      return baseOptions
    }
    return mergeEventTypeOptions(baseOptions, SUPPORT_BOOKED_EVENT_TYPE_OPTIONS_CLINIC_SUCCESS)
  }, [hasVcAuditAccess, userRoles])

  if (!hasVcAuditAccess && !hasSupportBookingsAccess) {
    return <Navigate to="/support-home" replace />
  }

  return (
    <LegacyBookedEventsView
      showHeader
      eventTypeOptions={allowedSupportEventTypes}
      hasVcAuditAccess={hasVcAuditAccess}
    />
  )
}
